import React from 'react';
import { useParams } from 'react-router';
import {
  Highlight,
  IconBannersWarning,
  LoadingSpinner,
  VStack,
} from '@adc/parallax-component-library';
import i18n from '../../utilities/i18n';
import { useQuery } from '@tanstack/react-query';
import { deviceByKeyQueryOptions } from '../../queryOptions';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import {
  useResolvedDeviceHierarchy,
  useSyncCurrentOrganization,
} from '../../hooks';
import DeviceHeader from './deviceComponents/DeviceHeader';

/**
 * Device component that displays the details of a specific device.
 * @returns {React.JSX.Element}
 */
export default function Device() {
  const { deviceKey } = useParams();

  const device = useQuery(
    deviceByKeyQueryOptions(deviceKey || '', {
      enabled: !!deviceKey,
    }),
  );

  const { subject, site, study, organization, organizationKey } =
    useResolvedDeviceHierarchy(device.data);

  useSyncCurrentOrganization(organizationKey);

  const hierarchyQueries = [subject, site, study, organization];
  const isHierarchyLoading = hierarchyQueries.some((query) => query.isLoading);
  const isHierarchyError = hierarchyQueries.some((query) => query.isError);

  const errorMessage = useErrorMessageOnEntityView(
    device.error || hierarchyQueries.find((query) => query.isError)?.error,
    'Page_Device.load_error',
  );

  return (
    <VStack gap="$2" height="100%">
      {device.isError || isHierarchyError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Device.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="device-error-banner"
        />
      ) : null}

      {device.isPending || isHierarchyLoading ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="device-loading" />
        </VStack>
      ) : null}

      {device.isSuccess && !isHierarchyLoading && !isHierarchyError ? (
        <VStack gap="$4">
          <DeviceHeader
            device={device.data}
            resolvedHierarchyNames={{
              organizationName: organization.data?.name,
              studyName: study.data?.name,
              siteName: site.data?.name,
              subjectName: subject.data?.name,
            }}
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
